import { useEffect, useRef, useState, type ReactElement } from 'react';
import { toast } from '../../../../design-system/components';
import { trackFailed } from '../../../../../../shared-web/track';
import { useSession } from '../../../auth';
import { apiErrorCode, apiFailureReason, emberApi, isAuthRequired, type FriendsPage as FriendsData, type GroupsList } from '../../api';
import { refreshChats, useChatsLive, useChatsSnapshot } from '../../chats-store';
import { BottomSheet } from '../../components/BottomSheet';
import { EmberDisc } from '../../components/EmberDisc';
import { Link } from '../../components/Link';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { PresenceAvatar } from '../../components/PresenceAvatar';
import { plural } from '../../format';
import { navigate } from '../../router';
import { Binoculars, CHEVRON, DashedPerson, Face, FLAME, FriendsBar, NotAvailable, Sparkle, dayLabel, firstName } from './friend-parts';
import { MeetPeopleIntro } from './MeetPeopleIntro';

type Sheet = { kind: 'friend' } | { kind: 'topic'; roomId: string };

const chatOf = (roomId: string): string => `/talk?mode=chat&room=${encodeURIComponent(roomId)}`;
/** A topic's square, in turn: its ground and its ink. */
const TOPIC_TINTS: Array<[string, string]> = [['#E3ECE0', '#24603B'], ['#FBE1CF', '#8A3A07'], ['#ECE6F6', '#5B4A8A']];

/**
 * Meet people — `/friends` (2026-10-26 founder, named so 2026-10-27; mocks Friends / FriendsAll). One page for both ways of meeting people:
 * one new friend at a time (looking, an introduction out, or none), the rooms by topic they hold (rooms they sit in,
 * topics still waiting for people, and adding one while they have space), and what came before (friends they met,
 * requests they stopped). Someone who may not take part gets `{ eligible: false }` and sees nothing of it.
 *
 * Groups (2026-11-03 founder; mock Main): someone with a group open to them (GET /api/groups `open`) first sees the three
 * ways — One person, Topic rooms, Groups — each a picture and a line; One person (`?view=one`) and Topic rooms
 * (`?view=rooms`) open this page's own part for it, unchanged; Groups opens /groups. With no group open to them the page
 * is as it was.
 */
export function FriendsPage({ view = null }: { view?: string | null } = {}): ReactElement {
  const { session } = useSession();
  const [page, setPage] = useState<FriendsData | null>(null);
  const [failed, setFailed] = useState(false);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [busy, setBusy] = useState(false);
  const [intro, setIntro] = useState(false);
  const [groups, setGroups] = useState<GroupsList | null>(null);
  const help = useRef<HTMLButtonElement>(null);

  // Reads overlap (the first, the live channel's, coming back to the tab): only the newest one's answer is the page.
  const reads = useRef(0);
  // The first read marks the page opened (friends.pageFor): the home's sign stops saying "new!" — read its list again.
  const been = useRef(false);
  const load = (): void => {
    const seq = ++reads.current;
    emberApi.friends()
      .then((r) => {
        if (r.eligible && !been.current) { been.current = true; void refreshChats(); }
        if (seq !== reads.current) return;
        setPage(r);
        setFailed(false);
      })
      .catch((err: unknown) => {
        if (seq !== reads.current) return;
        if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent('/friends')}`, { replace: true }); return; }
        trackFailed('friends_load', apiFailureReason(err));
        setFailed(true);
      });
    // Whether a group is open to them (the three ways in). Groups off (503 groups_unavailable) or the read failing leaves
    // the page as it was; a page already showing the ways keeps them.
    emberApi.groups()
      .then((g) => setGroups(g))
      .catch((err: unknown) => {
        if (apiErrorCode(err) !== 'groups_unavailable' && !isAuthRequired(err)) trackFailed('group_hub', apiFailureReason(err));
        setGroups((cur) => cur ?? { open: false });
      });
  };
  useEffect(() => {
    load();
    // An introduction lands, a room opens: the page reads again when the person comes back to it.
    const again = (): void => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', again);
    return () => document.removeEventListener('visibilitychange', again);
  }, []);
  // And while it stays on screen, every change the live channel announces reads it again (2026-10-27 walk-through: a topic
  // stopped on the phone stood on the desktop's page, 3 of 3, until a reload).
  useChatsLive(true);
  const chats = useChatsSnapshot().data;
  useEffect(() => { if (chats) load(); }, [chats]);

  const stop = async (target: Sheet): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      if (target.kind === 'friend') await emberApi.stopFriendRequest();
      else await emberApi.leaveRoom(target.roomId, false);
      setSheet(null);
      load();
    } catch (err: unknown) {
      trackFailed(target.kind === 'friend' ? 'friends_stop_looking' : 'friends_topic_stop', apiFailureReason(err));
      // Already stopped elsewhere (another device): the page reads again and there is nothing to try again.
      const gone = ['no_request', 'not_a_member', 'not_found'].includes(apiErrorCode(err) ?? '');
      setSheet(null);
      load();
      if (!gone) toast.error('Couldn’t stop that just now. Try again?');
    } finally {
      setBusy(false);
    }
  };

  if (!page && failed) return <PageFailure title="Couldn’t open this page" logState="load_failed" retry />;
  if (!page || !groups) return <PageLoading />;
  if (!page.eligible) return <NotAvailable />;

  const friend = page.friend ?? null;
  const rooms = page.rooms ?? { held: null, cap: null, up: [], sitting: [] };
  const met = page.earlier?.met ?? [];
  const stopped = page.earlier?.stopped ?? [];
  const firstVisit = !friend && !rooms.up.length && !rooms.sitting.length && !met.length && !stopped.length;
  // A greeting, not a count (2026-10-27 founder: "4 rooms so far!" read as all the rooms there are).
  const name = firstName(session?.self_name);
  // Which part is on screen: everything (no group open to them: the page as it was), the three ways, or one way's part.
  const part: 'all' | 'ways' | 'one' | 'rooms' = !groups.open ? 'all' : view === 'one' || view === 'rooms' ? view : 'ways';

  return (
    <main className="nf-page" data-log-seen={part === 'ways' ? 'group_hub' : 'friends_page'} data-log-state={part === 'ways' ? undefined : firstVisit ? 'first_visit' : (friend?.status || 'none')}>
      <FriendsBar to={part === 'one' || part === 'rooms' ? '/friends' : '/'} />
      <div className="nf-head">
        {part === 'one' || part === 'rooms'
          ? <h1 className="nf-title">{part === 'one' ? 'One person' : 'Topic rooms'}</h1>
          : (
            <>
              <span className="nf-kicker">hi {name || 'there'}!</span>
              {/* What this is is told by the intro behind the ?, not in words on the page (2026-10-27 founder). */}
              <div className="nf-title-row">
                <h1 className="nf-title">Meet people</h1>
                <button ref={help} type="button" className="nf-help" aria-label="What is Meet people?" data-log="friends_help" data-log-state="page" onClick={() => setIntro(true)}>
                  <span aria-hidden>?</span>
                </button>
              </div>
            </>
          )}
      </div>
      {part === 'ways' && groups.open && <Ways groups={groups} />}
      {part === 'one' && (firstVisit
        ? <FirstVisit rooms={false} />
        : (
          <div className="nf-main">
            <FriendSlot page={page} onStop={() => setSheet({ kind: 'friend' })} />
            <Earlier friend={friend} met={met} stopped={stopped} />
          </div>
        ))}
      {part === 'rooms' && (
        <div className="nf-main">
          <div className="nf-label">
            <span>YOUR ROOMS</span>
            {rooms.held !== null && rooms.cap !== null && <span className="nf-label-aside">{rooms.held} of {rooms.cap} places</span>}
          </div>
          <Rooms rooms={rooms} onStop={(roomId) => setSheet({ kind: 'topic', roomId })} />
        </div>
      )}
      {part === 'all' && (firstVisit
        ? <FirstVisit />
        : (
          <div className="nf-main">
            <div className="nf-label"><span>ONE PERSON</span><span className="nf-label-aside">one at a time</span></div>
            <FriendSlot page={page} onStop={() => setSheet({ kind: 'friend' })} />

            <div className="nf-label is-spaced">
              <span>YOUR ROOMS</span>
              {rooms.held !== null && rooms.cap !== null && <span className="nf-label-aside">{rooms.held} of {rooms.cap} places</span>}
            </div>
            <Rooms rooms={rooms} onStop={(roomId) => setSheet({ kind: 'topic', roomId })} />

            <Earlier friend={friend} met={met} stopped={stopped} />
          </div>
        ))}

      {sheet && (
        <BottomSheet
          title={sheet.kind === 'friend' ? 'Stop looking?' : 'Stop this topic?'}
          description="I’ll stop looking. Nobody hears about it. You can start again anytime."
          logSeen={sheet.kind === 'friend' ? 'friends_stop_looking_sheet' : 'friends_topic_stop_sheet'}
          onClose={() => setSheet(null)}
        >
          <button
            type="button"
            className="ember-btn nf-btn-dark"
            disabled={busy}
            data-log={sheet.kind === 'friend' ? 'friends_stop_looking' : 'friends_topic_stop'}
            data-log-state="confirm"
            data-log-match-id={sheet.kind === 'topic' ? sheet.roomId : undefined}
            onClick={() => { void stop(sheet); }}
          >
            Stop looking
          </button>
          <button
            type="button"
            className="ember-btn ember-btn-ghost"
            data-log={sheet.kind === 'friend' ? 'friends_stop_looking' : 'friends_topic_stop'}
            data-log-state="keep"
            onClick={() => setSheet(null)}
          >
            Keep looking
          </button>
        </BottomSheet>
      )}
      {intro ? <MeetPeopleIntro from="page" onClose={() => setIntro(false)} foldTo={() => help.current} /> : null}
    </main>
  );
}

/** What came before: friends they met (their chat, still theirs to open) and requests they stopped (to start again). */
function Earlier({ friend, met, stopped }: { friend: FriendsData['friend'] | null; met: NonNullable<FriendsData['earlier']>['met']; stopped: NonNullable<FriendsData['earlier']>['stopped'] }): ReactElement | null {
  if (!met.length && !stopped.length) return null;
  return (
    <>
      <div className="nf-label is-spaced"><span>EARLIER</span></div>
      <section className="nf-list is-soft">
        {met.map((m) => (
          <div className="nf-row is-tight" key={m.request_id}>
            {m.them ? <Face avatar={m.them.avatar} size={44} /> : <span className="nf-row-icon is-round"><DashedPerson size={22} color="#8A8170" /></span>}
            <span className="nf-row-text">
              <span className="nf-row-name">{m.them?.name || 'Someone'}</span>
              <span className="nf-row-sub">Met {dayLabel(m.at)}</span>
            </span>
            {/* A chat that wrapped up stays theirs to read (the check-in says so): its room's page, read-only. */}
            {m.room_id && m.room_status && (
              <Link to={m.room_status === 'open' ? chatOf(m.room_id) : `/rooms/${encodeURIComponent(m.room_id)}`} className="nf-row-go" data-log="friends_earlier_chat" data-log-state={m.room_status} data-log-match-id={m.room_id}>Chat</Link>
            )}
          </div>
        ))}
        {stopped.map((s) => (
          <div className="nf-row is-tight" key={s.request_id}>
            <span className="nf-row-text">
              <span className="nf-row-what">{s.card.topics?.length ? `Into ${s.card.topics.join(' · ')}` : 'Someone'}</span>
              <span className="nf-row-when">You stopped {dayLabel(s.at)}</span>
            </span>
            {/* While a request stands there is no other to start (one at a time): its card is edited instead. */}
            {!friend && <Link to={`/friends/new?from=${encodeURIComponent(s.request_id)}`} className="nf-pill" data-log="friends_start_again" data-log-match-id={s.request_id}>Start again</Link>}
          </div>
        ))}
      </section>
    </>
  );
}

/**
 * A first visit (Friends): nothing under way and nothing earlier — what this is, and the two ways in. On One person's own
 * part (the three ways, Groups 2026-11-03) the way to rooms is left out: rooms have their own part.
 */
function FirstVisit({ rooms = true }: { rooms?: boolean } = {}): ReactElement {
  return (
    <div className="nf-main is-roomy">
      <section className="nf-hero">
        <div className="nf-hero-band">
          <svg className="nf-hero-art" viewBox="0 0 358 150" preserveAspectRatio="none" aria-hidden>
            <circle cx="40" cy="130" r="80" fill="#F6EAD0" />
            <circle cx="330" cy="14" r="64" fill="#E3ECE0" />
            <path d="M96 84c46-44 136-44 176 0" stroke="#E2A23C" strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="1 7" />
          </svg>
          <span className="nf-hero-me nf-bob"><EmberDisc className="nf-hero-disc" /></span>
          <span className="nf-hero-them nf-bob is-late"><DashedPerson size={36} color="#8A8170" /></span>
          <span className="nf-hero-note">who could it be?</span>
          <Sparkle size={14} className="nf-twinkle nf-hero-spark1" />
          <Sparkle size={10} color="#F4720C" className="nf-twinkle is-late nf-hero-spark2" />
        </div>
        <div className="nf-hero-copy">
          <h2 className="nf-h2">Meet someone fun to chat with</h2>
          <p>One person, picked by me. Everyday stuff, at your own pace.</p>
        </div>
        <div className="nf-hero-go">
          <Link to="/friends/new" className="nf-cta" data-log="friends_start" data-log-state="first_visit">Let’s go</Link>
          <span className="nf-hand is-quiet">3 quick taps</span>
        </div>
      </section>

      <section className="nf-tiles">
        <div className="nf-tile is-tilt-l">
          <span className="nf-tile-icon" style={{ background: '#FBE1CF' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden><circle cx="8" cy="9" r="3" stroke="#8A3A07" strokeWidth="1.8" fill="none" /><circle cx="16" cy="9" r="3" stroke="#8A3A07" strokeWidth="1.8" fill="none" /><path d="M3 19c.8-2.8 2.8-4.3 5-4.3s4.2 1.5 5 4.3M11 19c.8-2.8 2.8-4.3 5-4.3s4.2 1.5 5 4.3" stroke="#8A3A07" strokeWidth="1.8" fill="none" strokeLinecap="round" /></svg>
          </span>
          <span>Just you two, and me</span>
        </div>
        <div className="nf-tile is-tilt-r">
          <span className="nf-tile-icon" style={{ background: '#FFF1C7' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden><circle cx="12" cy="12" r="8" stroke="#8A5A12" strokeWidth="1.8" fill="none" /><path d="M12 7.5V12l3 2" stroke="#8A5A12" strokeWidth="1.8" fill="none" strokeLinecap="round" /></svg>
          </span>
          <span>Reply whenever</span>
        </div>
        <div className="nf-tile is-tilt-s">
          <span className="nf-tile-icon" style={{ background: '#E3ECE0' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden><path d="M7 12.5l3.2 3.2L17 9" stroke="#24603B" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="8" stroke="#24603B" strokeWidth="1.8" fill="none" /></svg>
          </span>
          <span>Stop anytime</span>
        </div>
      </section>

      {rooms && <Link to="/friends/rooms/new" className="nf-roomlink" data-log="friends_topic_add" data-log-state="first_visit">
        <span className="nf-roomlink-pic" aria-hidden>
          <span className="nf-roomlink-dot is-a" />
          <span className="nf-roomlink-dot is-b" />
          <span className="nf-roomlink-flame">{FLAME}</span>
        </span>
        <span className="nf-roomlink-text">
          <span className="nf-roomlink-title">Or a room on one topic</span>
          {/* What a room is, not the cap (2026-10-26 walk-through: "Up to 3 at a time" read as three people) */}
          <span className="nf-roomlink-sub">3 to 8 people, plus me</span>
        </span>
        <span className="nf-roomlink-go">{CHEVRON}</span>
      </Link>}
    </div>
  );
}

/**
 * The three ways to meet people (Groups 2026-11-03; mock Main): one person, topic rooms, groups — each a picture, a line
 * or two, and the way in. Groups says what is new there for them (posts by others they have not opened), or that it is
 * new while they have joined none.
 */
function Ways({ groups }: { groups: Extract<GroupsList, { open: true }> }): ReactElement {
  const joined = groups.groups.filter((g) => g.joined);
  const fresh = joined.reduce((n, g) => n + g.fresh, 0);
  const badge = fresh > 0 ? `${fresh} new` : joined.length === 0 ? 'NEW' : '';
  return (
    <div className="nf-main nf-ways">
      <Link to="/friends?view=one" className="nf-way" data-log="group_hub_open" data-log-state="one">
        <span className="nf-way-band" style={{ background: '#FBE1CF' }}>
          <svg viewBox="0 0 358 92" preserveAspectRatio="xMidYMid slice" aria-hidden>
            <circle cx="40" cy="96" r="62" fill="#F8D2B6" />
            <path d="M120 54c34-28 86-28 118 0" stroke="#E2A23C" strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="1 7" />
            <circle cx="112" cy="58" r="16" fill="#F4720C" />
            <circle cx="246" cy="58" r="16" fill="none" stroke="#8A3A07" strokeWidth="2" strokeDasharray="3 4" />
            <path d="M179 30c4 6 8 9 8 15a8 8 0 0 1-16 0c0-6 4-9 8-15z" fill="#FFB010" />
          </svg>
        </span>
        <span className="nf-way-row">
          <span className="nf-way-text">
            <span className="nf-way-title">One person</span>
            <span className="nf-way-sub">Ember picks someone you’d get along with. Just you two, reply whenever.</span>
          </span>
          <span className="nf-way-chev">{CHEVRON}</span>
        </span>
      </Link>
      <Link to="/friends?view=rooms" className="nf-way" data-log="group_hub_open" data-log-state="rooms">
        <span className="nf-way-band" style={{ background: '#E3ECE0' }}>
          <svg viewBox="0 0 358 92" preserveAspectRatio="xMidYMid slice" aria-hidden>
            <circle cx="320" cy="6" r="54" fill="#D2E1CD" />
            <rect x="128" y="22" width="102" height="56" rx="12" fill="#FFFDF8" />
            <path d="M150 78l-8 12 18-12z" fill="#FFFDF8" />
            <circle cx="156" cy="50" r="8" fill="#24603B" />
            <circle cx="179" cy="50" r="8" fill="#E2A23C" />
            <circle cx="202" cy="50" r="8" fill="#8A3A07" />
          </svg>
        </span>
        <span className="nf-way-row">
          <span className="nf-way-text">
            <span className="nf-way-title">Topic rooms</span>
            <span className="nf-way-sub">A small chat about one thing you love. 3 to 8 people, plus Ember.</span>
          </span>
          <span className="nf-way-chev">{CHEVRON}</span>
        </span>
      </Link>
      <Link to="/groups" className="nf-way" data-log="group_hub_open" data-log-state="groups">
        <span className="nf-way-band" style={{ background: '#F6EAD0' }}>
          <svg viewBox="0 0 358 92" preserveAspectRatio="xMidYMid slice" aria-hidden>
            <circle cx="30" cy="10" r="46" fill="#EFDCB4" />
            <rect x="146" y="20" width="66" height="44" rx="12" fill="#FFFDF8" />
            <path d="M168 64l-6 10 16-10z" fill="#FFFDF8" />
            <path d="M172 38c0-5 9-5 9 1 0 4-5 4-5 9" stroke="#B94B0A" strokeWidth="2.4" fill="none" strokeLinecap="round" />
            <circle cx="176" cy="54" r="1.8" fill="#B94B0A" />
            <circle cx="104" cy="44" r="10" fill="#24603B" />
            <circle cx="122" cy="72" r="8" fill="#E2A23C" />
            <circle cx="248" cy="40" r="9" fill="#8A3A07" />
            <circle cx="262" cy="70" r="11" fill="#F4720C" />
            <circle cx="86" cy="76" r="6" fill="#C9B48A" />
            <circle cx="282" cy="22" r="6" fill="#C9B48A" />
          </svg>
          {badge && <span className="nf-way-badge">{badge}</span>}
        </span>
        <span className="nf-way-row">
          <span className="nf-way-text">
            <span className="nf-way-title">Groups</span>
            <span className="nf-way-sub">People who’ve been through the same thing. Ask what’s on your mind, answer when you can.</span>
          </span>
          <span className="nf-way-chev">{CHEVRON}</span>
        </span>
      </Link>
    </div>
  );
}

/** The one new friend: looking (since, topics, Edit, Stop looking), an introduction out, or none. */
function FriendSlot({ page, onStop }: { page: FriendsData; onStop: () => void }): ReactElement {
  const friend = page.friend ?? null;
  if (friend?.status === 'looking') {
    const topics = friend.card.topics || [];
    return (
      <section className="nf-card nf-looking">
        <div className="nf-looking-top">
          <span className="nf-looking-pic">
            <Face avatar={friend.card.avatar} size={56} className="nf-bob is-ring-peach" />
            <Binoculars width={34} className="nf-looking-bino" />
          </span>
          <span className="nf-looking-text">
            <span className="nf-hand">sniffing around...</span>
            <span className="nf-row-sub">Since {dayLabel(friend.since)}{topics.length ? ` · ${topics.join(', ')}` : ''}</span>
            <span className="nf-row-sub">It can take a while. I’ll let you know the moment I find someone.</span>
          </span>
        </div>
        <div className="nf-two">
          <Link to="/friends/new?edit=1" className="nf-soft-btn" data-log="friends_edit">Edit</Link>
          <button type="button" className="nf-soft-btn is-plain" data-log="friends_stop_looking" data-log-state="ask" onClick={onStop}>Stop looking</button>
        </div>
      </section>
    );
  }
  if (friend?.status === 'found') {
    const roomId = friend.intro?.room_id ?? friend.room_id;
    const them = friend.intro?.them ?? null;
    const waiting = friend.intro?.me.answer === 'yes';
    const body = (
      <>
        {them ? <Face avatar={them.avatar} size={50} className="is-ring-white" /> : <span className="nf-row-icon is-round is-white"><DashedPerson size={26} color="#B94B0A" /></span>}
        <span className="nf-row-text">
          <span className="nf-row-name is-big">{them?.name || 'Someone new'}</span>
          <span className="nf-row-sub is-ink">{waiting ? `Waiting for ${them?.name || 'them'}` : 'Ember found someone you’d get along with!'}</span>
        </span>
        <span className="nf-row-chev">{CHEVRON}</span>
      </>
    );
    return roomId
      ? <Link to={`/friends/intro/${encodeURIComponent(roomId)}`} className="nf-found" data-log="friends_intro_open" data-log-state={waiting ? 'waiting' : 'found'} data-log-match-id={roomId}>{body}</Link>
      : <div className="nf-found">{body}</div>;
  }
  // "Another" only after a friend was met; a request stopped before anyone was found met no one.
  const before = (page.earlier?.met.length ?? 0) > 0;
  return (
    <Link to="/friends/new" className="nf-again" data-log="friends_start" data-log-state="again">
      <span className="nf-row-icon is-round is-white is-big"><DashedPerson size={26} color="#B94B0A" /></span>
      <span className="nf-row-text">
        <span className="nf-again-title">{before ? 'Meet someone else?' : 'Meet someone new?'}</span>
        <span className="nf-hand is-small is-dark">whenever you feel like it</span>
      </span>
      <span className="nf-row-chev is-dark">{CHEVRON}</span>
    </Link>
  );
}

/** Rooms they sit in, their topics still waiting for people, and adding one while they have space. */
function Rooms({ rooms, onStop }: { rooms: NonNullable<FriendsData['rooms']>; onStop: (roomId: string) => void }): ReactElement {
  const canAdd = rooms.cap === null || rooms.held === null || rooms.held < rooms.cap;
  return (
    <>
      <section className="nf-list">
        {rooms.sitting.map((r) => (
          <Link to={chatOf(r.room_id)} className="nf-row" key={r.room_id} data-log="friends_room_open" data-log-match-id={r.room_id}>
            <span className="nf-huddle" aria-hidden>
              {r.people.slice(0, 2).map((p, i) => (
                <span className={`nf-huddle-face is-${i ? 'b' : 'a'}`} key={`${p.name}-${i}`}><PresenceAvatar avatarKey={p.avatar} size={22} /></span>
              ))}
              <span className="nf-huddle-flame">{FLAME}</span>
            </span>
            <span className="nf-row-text">
              <span className="nf-row-name">{r.name || r.topic}</span>
              {/* Who is in it with you (2026-10-27 founder: "3 of you" read as a room not yet joined) */}
              <span className="nf-row-sub">{r.people.length ? `You and ${plural(r.people.length, 'other')}` : 'Just you'}{r.unread > 0 ? ' · new messages' : ''}</span>
            </span>
            <span className="nf-row-go">Open</span>
          </Link>
        ))}
        {rooms.up.map((t, i) => {
          const [ground, ink] = TOPIC_TINTS[i % TOPIC_TINTS.length];
          return (
            <div className="nf-row" key={t.room_id}>
              <span className="nf-row-icon" style={{ background: ground }}><DashedPerson size={22} color={ink} /></span>
              <span className="nf-row-text">
                <span className="nf-row-name">{t.topic || t.name}</span>
                {/* As the apps say it (2026-10-26 walk-through: "1 of 3 in" sat beside the section's "1 of 3" places) */}
                <span className={`nf-row-sub${t.present > 1 ? ' is-hot' : ''}`}>{t.present > 1 ? `${t.present - 1} ${t.present === 2 ? 'other wants' : 'others want'} this too!` : `Looking since ${dayLabel(t.since)}`}</span>
              </span>
              <button type="button" className="nf-pill is-small" data-log="friends_topic_stop" data-log-state="ask" data-log-match-id={t.room_id} onClick={() => onStop(t.room_id)}>Stop</button>
            </div>
          );
        })}
        {canAdd && (
          <Link to="/friends/rooms/new" className="nf-row is-add" data-log="friends_topic_add" data-log-state="rooms">
            <span className="nf-add-box" aria-hidden>
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
            </span>
            <span className="nf-row-name">Add a topic</span>
          </Link>
        )}
      </section>
      <p className="nf-note">{canAdd ? 'A room is 3 to 8 people who picked the same thing, plus me.' : `That’s ${rooms.cap}! Leave a room or stop a topic to add another.`}</p>
    </>
  );
}
